import { ArrowRightIcon } from '@phosphor-icons/react'
import * as Tabs from '@radix-ui/react-tabs'
import { Link, useSearchParams } from 'react-router'
import { z } from 'zod'
import { BeaconRunCoverage } from '../../components/beacon/beacon-run-coverage.tsx'
import { BeaconRunReplay } from '../../components/beacon/beacon-run-replay.tsx'
import { RunSummaryStrip } from '../../components/beacon/beacon-run-summary-strip.tsx'
import { verifiedFindingsOf } from '../../components/beacon/beacon-run.ts'
import { Routes } from '../../routes.ts'
import { getBeaconRunScanId } from '../../utils/api-client/api-hooks.ts'
import type { BeaconRun } from '../../utils/api-client/api-schemas.ts'
import * as styles from './beacon-run-page.css.ts'

export const BeaconRunViewSchema = z.enum(['coverage', 'replay'])
export type BeaconRunView = z.infer<typeof BeaconRunViewSchema>

/** The completed run's open view, in the URL so a link can open the replay directly. */
export const BEACON_RUN_VIEW_PARAM = 'view'

const VIEW_LABELS: Record<BeaconRunView, string> = { coverage: 'Coverage', replay: 'Run replay' }

const DEFAULT_VIEW: BeaconRunView = 'coverage'

/**
 * A completed run: its totals and the way to its findings, then what it examined (with the leads it
 * dismissed) or the run replayed from its stream.
 */
export const BeaconRunCompleted: React.FC<{ run: BeaconRun }> = ({ run }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const view = BeaconRunViewSchema.safeParse(searchParams.get(BEACON_RUN_VIEW_PARAM)).data ?? DEFAULT_VIEW
  const findings = verifiedFindingsOf(run) ?? 0
  const scanId = getBeaconRunScanId(run)

  const setView = (next: string) =>
    setSearchParams(previous => {
      const params = new URLSearchParams(previous)
      if (BeaconRunViewSchema.parse(next) === DEFAULT_VIEW) {
        params.delete(BEACON_RUN_VIEW_PARAM)
      } else {
        params.set(BEACON_RUN_VIEW_PARAM, next)
      }
      return params
    })

  return (
    <>
      <div className={styles.completedHead}>
        <RunSummaryStrip run={run} />
        {scanId ? (
          <Link className={styles.findingsLink} to={Routes.ScanDetailsPage.createPath({ scanId })}>
            Findings ({findings})
            <ArrowRightIcon aria-hidden size={18} weight="bold" />
          </Link>
        ) : (
          <p className={styles.note}>The run’s scan is no longer available.</p>
        )}
      </div>
      <Tabs.Root value={view} onValueChange={setView}>
        <Tabs.List className={styles.viewTabs} aria-label="Beacon run views">
          {BeaconRunViewSchema.options.map(option => (
            <Tabs.Trigger key={option} value={option} className={styles.viewTab}>
              {VIEW_LABELS[option]}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        <Tabs.Content value="coverage" tabIndex={-1} className={styles.viewBody}>
          <BeaconRunCoverage runId={run.id} scanId={scanId} />
        </Tabs.Content>
        <Tabs.Content value="replay" tabIndex={-1} className={styles.viewBody}>
          <BeaconRunReplay run={run} />
        </Tabs.Content>
      </Tabs.Root>
    </>
  )
}
